import {
  shouldForceHydrateRestoredDocChatTurn,
  shouldPollRestoredDocChatTurn,
} from 'features/embeddedSidebar/sessions/documentChatConversationHydration'
import { documentChatRestoreMode } from 'features/embeddedSidebar/sessions/documentChatSessionSchemas'
import { getDocChatTurnStatus } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatConversation'
import { isDocChatTurnInFlight } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatInFlightStore'
import type { DocChatConversation } from 'features/knowledgeDocumentViewer/types'
import { useEffect, useMemo, useRef } from 'react'

import type { RecordedAgenticChatSession } from './useAgenticChatSessionHistory'

export type RestoredDocChatTurnSettlement = {
  conversation: DocChatConversation
  sessionKey: string
  tabId?: string
}

function restoredRunningOverlayTurnId(
  conversation: DocChatConversation
): string | undefined {
  const turnId = conversation.activeTurnId
  if (!turnId) return undefined
  const turn = conversation.turns.find((candidate) => candidate.id === turnId)
  if (!turn?.restoredRunning) return undefined
  if (getDocChatTurnStatus(turn) !== 'loading') return undefined
  return turnId
}

function restoredTurnFlightState(
  live: RecordedAgenticChatSession,
  persisted: RecordedAgenticChatSession
): {
  restoreMode: ReturnType<typeof documentChatRestoreMode>
  isTurnInFlight: boolean
  hasRestoredRunningOverlay: boolean
} {
  const overlayTurnId = restoredRunningOverlayTurnId(live.conversation)
  const liveTurnId = live.conversation.activeTurnId
  return {
    restoreMode: documentChatRestoreMode(persisted),
    isTurnInFlight: Boolean(
      liveTurnId &&
      isDocChatTurnInFlight(live.conversation, liveTurnId, live.sourceTabId)
    ),
    hasRestoredRunningOverlay: overlayTurnId !== undefined,
  }
}

export function useRestoredDocChatTurnSettlement({
  hydrateSession,
  liveSessions,
  onRestoredTurnSettled,
  persistedSessions,
  refreshActive,
  refreshPinned,
}: {
  hydrateSession: (
    session: RecordedAgenticChatSession,
    options?: { force?: boolean }
  ) => Promise<DocChatConversation | null>
  liveSessions: RecordedAgenticChatSession[]
  onRestoredTurnSettled?: (update: RestoredDocChatTurnSettlement) => void
  persistedSessions: RecordedAgenticChatSession[]
  refreshActive: () => Promise<unknown>
  refreshPinned: () => Promise<unknown>
}): void {
  const forceHydrateInFlight = useRef(new Set<string>())

  const shouldPollRestoredTurns = useMemo(
    () =>
      liveSessions.some((live) => {
        const persisted = persistedSessions.find(
          (session) => session.key === live.key
        )
        if (!persisted) return false
        const state = restoredTurnFlightState(live, persisted)
        return shouldPollRestoredDocChatTurn(state)
      }),
    [liveSessions, persistedSessions]
  )

  useEffect(() => {
    if (!shouldPollRestoredTurns) return
    const intervalId = window.setInterval(() => {
      void refreshActive()
      void refreshPinned()
    }, 1000)
    return () => window.clearInterval(intervalId)
  }, [refreshActive, refreshPinned, shouldPollRestoredTurns])

  useEffect(() => {
    liveSessions.forEach((live) => {
      const persisted = persistedSessions.find(
        (session) => session.key === live.key
      )
      if (!persisted) return
      const state = restoredTurnFlightState(live, persisted)
      if (!shouldForceHydrateRestoredDocChatTurn(state)) return
      if (forceHydrateInFlight.current.has(live.key)) return
      forceHydrateInFlight.current.add(live.key)
      void hydrateSession(persisted, { force: true })
        .then((conversation) => {
          if (!conversation) return
          onRestoredTurnSettled?.({
            conversation,
            sessionKey: live.key,
            tabId: live.sourceTabId,
          })
        })
        .finally(() => {
          forceHydrateInFlight.current.delete(live.key)
        })
    })
  }, [hydrateSession, liveSessions, onRestoredTurnSettled, persistedSessions])
}
